/**
 * Runtime switches for the community integrations pack.
 *
 * Resolution order (highest priority first):
 *   1. localStorage override: `vn2r.features.<key>` = `true` / `false`
 *   2. Vite environment variable: `VITE_VN2R_FEATURE_<KEY>`
 *   3. the safe project default below
 *
 * Keeping every integration behind its own switch makes rollback possible
 * without reverting unrelated work or deleting locally stored data.
 */

// Ligado por defeito: acrescenta a uma coisa que já existe e falha para
// o comportamento anterior. Desligado por defeito: é território novo,
// ninguém o pediu ainda, e cada botão no friso custa atenção a quem lá
// procura outra coisa. Desligado NÃO quer dizer meio feito: quem quiser
// experimentar liga a flag e usa. A decisão de o promover a permanente
// é de produto, não de código.
export const featureDefaults = {
  // Reutilizar a derivação do próprio IFC em cache, e poder guardar o
  // modelo activo em `.frag`. Em ambos os casos o IFC continua lá, por
  // isso nada do que o lê fica sem chão.
  nativeFragments: true,
  // ABRIR um `.frag` como modelo é outra coisa: não traz IFC. Ligado
  // com as declarações que dizem isso a quem o abre, em vez de deixar
  // o IDS, o BoQ, a Tabela e o assistente responderem zero como se o
  // modelo estivesse vazio. Ver `modelFileTypes.ts`.
  openFragments: true,
  enhancedCamera: true,
  materialTransparency: true,
  bcfPins: true,
  versionHistory: true,
  visibilityClashes: true,
  pdfReview: true,
  mcpServer: true,
  pointClouds: true,
  graphRag: true,
  circularity: true,
} as const

export type FeatureKey = keyof typeof featureDefaults

const STORAGE_PREFIX = 'vn2r.features.'

function envName(key: FeatureKey): string {
  return `VITE_VN2R_FEATURE_${key.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase()}`
}

function parseBoolean(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === '1') return true
  if (value === false || value === 'false' || value === '0') return false
  return null
}

function storageValue(key: FeatureKey): boolean | null {
  try {
    return parseBoolean(localStorage.getItem(`${STORAGE_PREFIX}${key}`))
  } catch {
    return null
  }
}

function environmentValue(key: FeatureKey): boolean | null {
  const env = import.meta.env as Record<string, unknown>
  return parseBoolean(env[envName(key)])
}

export function isFeatureEnabled(key: FeatureKey): boolean {
  return (
    storageValue(key) ?? environmentValue(key) ?? featureDefaults[key]
  )
}

export function setFeatureOverride(
  key: FeatureKey,
  enabled: boolean | null,
): void {
  try {
    const storageKey = `${STORAGE_PREFIX}${key}`
    if (enabled === null) localStorage.removeItem(storageKey)
    else localStorage.setItem(storageKey, String(enabled))
  } catch {
    // A blocked localStorage must never prevent the viewer from starting.
  }
}

export function featureSnapshot(): Record<FeatureKey, boolean> {
  return Object.fromEntries(
    (Object.keys(featureDefaults) as FeatureKey[]).map((key) => [
      key,
      isFeatureEnabled(key),
    ]),
  ) as Record<FeatureKey, boolean>
}
